import type { SearchPayload } from '@civilgrid/ui/hooks';
import type { IntelligenceSummary, PlanStatus } from '~/common/types';
import { getAxiosClient } from './common';
import { transformSummary } from './projects2';

const axios = getAxiosClient();

/** Search payload extended with summaryIds narrowing (compute-status polling). */
export type SummariesSearchPayload = SearchPayload & {
  summaryIds?: string[];
};

export type IntelligenceSummariesApiResponse = {
  summaries: IntelligenceSummary[];
  count: number;
};

/** Org-wide table metadata, always unfiltered by design. */
export type IntelligenceSummaryMeta = {
  /** Total summary count (export-all row counts). */
  total: number;
  /** Distinct construction years ascending (Years filter dropdown options). */
  constructionYears: string[];
};

/** Column defs for server-side export; may be nested to mirror grouped headers. */
export type SummaryExportColumnDef = {
  id: string;
  header: string;
  columns?: SummaryExportColumnDef[];
};

/**
 * Fetch intelligence summaries for the org with server-side pagination, sort,
 * filters, globalFilter, and optional summaryIds narrowing.
 * Uses GET /v3/projects/intelligenceSummary; returns { summaries, count }.
 */
export async function fetchIntelligenceSummaries(
  organizationId: string,
  searchPayload: SummariesSearchPayload,
  { signal }: { signal?: AbortSignal } = {},
): Promise<IntelligenceSummariesApiResponse> {
  const params = new URLSearchParams({
    organizationId,
    search: JSON.stringify(searchPayload),
  });

  const { data } = await axios({
    method: 'GET',
    url: `/v3/projects/intelligenceSummary?${params.toString()}`,
    signal,
  });

  const summaries = (data.summaries ?? []).map((s: unknown) => transformSummary(s));
  const count = typeof data.count === 'number' ? data.count : summaries.length;
  return { summaries, count };
}

/**
 * Fetch org-wide table metadata (total count + distinct construction years).
 * Takes no search payload by design: values always reflect the full org
 * dataset regardless of table filters.
 * Uses GET /v3/projects/intelligenceSummary/meta.
 */
export async function fetchIntelligenceSummaryMeta(
  organizationId: string,
  { signal }: { signal?: AbortSignal } = {},
): Promise<IntelligenceSummaryMeta> {
  const params = new URLSearchParams({ organizationId });
  const { data } = await axios({
    method: 'GET',
    url: `/v3/projects/intelligenceSummary/meta?${params.toString()}`,
    signal,
  });
  return data as IntelligenceSummaryMeta;
}

/** Bulk construction-date selection: explicit summary ids, or all rows matching the table's search. */
export type BulkConstructionDatesSelection =
  | { mode: 'partial'; summaryIds: string[] }
  | { mode: 'all'; excludedSummaryIds?: string[]; search: SummariesSearchPayload };

export type BulkConstructionDatesUpdates = {
  /** Without an explicit end date, each project's end shifts to preserve its own duration (6 months when it has none). */
  constructionStartDate?: string;
  constructionEndDate?: string;
  /** Sets start to Jan 1 of the year and shifts end to preserve each project's duration (explicit dates win). */
  constructionYear?: number;
};

export type BulkConstructionDatesResponse = {
  updatedCount: number;
  /** Affected summary ids — poll these through the recompute. */
  summaryIds: string[];
  /** Projects whose recompute job was enqueued (ineligible projects are skipped, not failed). */
  dispatchedCount: number;
  /** Projects whose recompute dispatch threw; their rows keep stale conflict data until the next recompute. */
  dispatchFailedCount: number;
};

/**
 * Apply construction dates to every selected summary's project and trigger the
 * intelligence recompute for each. POST /v3/projects/intelligenceSummary/bulkUpdateConstructionDates.
 */
export async function bulkUpdateConstructionDates(
  organizationId: string,
  payload: { selection: BulkConstructionDatesSelection; updates: BulkConstructionDatesUpdates },
  { signal }: { signal?: AbortSignal } = {},
): Promise<BulkConstructionDatesResponse> {
  const params = new URLSearchParams({ organizationId });
  const { data } = await axios({
    method: 'POST',
    url: `/v3/projects/intelligenceSummary/bulkUpdateConstructionDates?${params.toString()}`,
    data: payload,
    signal,
  });
  return data as BulkConstructionDatesResponse;
}

/** Bulk row selection shared by the intelligenceSummary bulk endpoints. */
export type BulkSummariesSelection = BulkConstructionDatesSelection;

export type BulkPlanStatusUpdates = { planStatus: PlanStatus };

export type BulkPlanStatusResponse = {
  updatedCount: number;
  /** Affected summary ids. Plan status is not a conflict-trigger field, so no polling is needed. */
  summaryIds: string[];
};

/**
 * Apply one plan status to every selected summary's project.
 * POST /v3/projects/intelligenceSummary/bulkUpdatePlanStatus.
 */
export async function bulkUpdatePlanStatus(
  organizationId: string,
  payload: { selection: BulkSummariesSelection; updates: BulkPlanStatusUpdates },
  { signal }: { signal?: AbortSignal } = {},
): Promise<BulkPlanStatusResponse> {
  const params = new URLSearchParams({ organizationId });
  const { data } = await axios({
    method: 'POST',
    url: `/v3/projects/intelligenceSummary/bulkUpdatePlanStatus?${params.toString()}`,
    data: payload,
    signal,
  });
  return data as BulkPlanStatusResponse;
}

/**
 * Export summaries to Excel server-side (all rows matching the search payload;
 * the server splits sheets by construction year). Returns a raw xlsx buffer;
 * caller triggers the download (e.g. downloadExcelFileBuffer).
 */
export async function exportIntelligenceSummariesV3(
  organizationId: string,
  payload: { columnDefs: SummaryExportColumnDef[]; search: SummariesSearchPayload },
  { signal }: { signal?: AbortSignal } = {},
): Promise<ArrayBuffer> {
  const params = new URLSearchParams({ organizationId });
  const { data } = await axios({
    method: 'POST',
    url: `/v3/projects/intelligenceSummary/export/xlsx?${params.toString()}`,
    data: payload,
    responseType: 'arraybuffer',
    signal,
  });
  return data as ArrayBuffer;
}
